import { cn, Avatar, AvatarImage, AvatarFallback } from "@semo/shared-ui";

interface ChatNotificationToastProps {
  senderName: string;
  message: string;
  avatarUrl?: string;
  timestamp?: string; // Optional, usually 'Just now'
  onClick?: () => void;
  onDismiss?: () => void;
}

export const ChatNotificationToast = ({
  senderName,
  message,
  avatarUrl,
  timestamp,
  onClick,
}: ChatNotificationToastProps) => {
  return (
    <div
      className={cn(
        // Layout & Size
        "pointer-events-auto flex items-center w-[400px] max-w-[95vw] gap-5 p-5",
        // Styling
        "rounded-2xl bg-white shadow-xl ring-1 ring-black/5",
        "transition-all duration-300 hover:bg-gray-50 hover:scale-[1.01] active:scale-[0.98]",
        "cursor-pointer select-none",
        // Dark Mode
        "dark:bg-zinc-900 dark:ring-white/10 dark:hover:bg-zinc-800",
      )}
      onClick={onClick}
      role="alert"
    >
      {/* Avatar Section */}
      <Avatar className="h-12 w-12 shrink-0 border border-gray-100 shadow-sm dark:border-zinc-800">
        {avatarUrl && (
          <AvatarImage
            src={avatarUrl}
            alt={senderName}
            className="object-cover"
          />
        )}
        <AvatarFallback className="bg-primary/10 text-primary font-semibold text-base">
          {senderName.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      {/* Content Section */}
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate leading-none">
            {senderName}
          </p>
          {timestamp && (
            <span className="text-2xs font-medium text-gray-400 dark:text-gray-500 shrink-0">
              {timestamp}
            </span>
          )}
        </div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300 line-clamp-2 leading-snug">
          {message}
        </p>
      </div>
    </div>
  );
};
